import { Plus } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Pagination } from '@/components/ui/pagination'
import { SearchInput } from '@/components/ui/search-input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TableSkeletonRows } from '@/components/ui/table-skeleton'
import { useTableSearch } from '@/hooks/useTableSearch'
import { type NaicCompany, useNaicCompanies } from '@/models/naicCompany'
import { NaicCompanyDialog } from './NaicCompanyDialog'

const companySearchValues = (company: NaicCompany) => [company.name, company.naicCode]

/** The NAIC tab: the codes certificates print, which staff add to and correct. */
export function NaicCompanies() {
  const { companies, isLoading } = useNaicCompanies()
  const { query, onQueryChange, page, setPage, totalPages, pageItems, filtered, rangeSummary } =
    useTableSearch({
      items: companies,
      getSearchValues: companySearchValues,
    })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<NaicCompany | null>(null)

  function openDialog(company: NaicCompany | null) {
    setEditing(company)
    setDialogOpen(true)
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <SearchInput
          value={query}
          onValueChange={onQueryChange}
          placeholder="Search by name or NAIC code…"
          aria-label="Search NAIC companies"
          className="max-w-sm"
        />
        <Button onClick={() => openDialog(null)}>
          <Plus className="mr-1 size-4" /> Add company
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <span role="status" className="sr-only">
            Loading NAIC companies
          </span>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>NAIC code</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableSkeletonRows columns={2} />
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} className="text-muted-foreground text-center">
                  {query ? `No companies match “${query}”.` : 'No NAIC companies yet.'}
                </TableCell>
              </TableRow>
            ) : (
              pageItems.map((company) => (
                <TableRow key={company.id} className="cursor-pointer" onClick={() => openDialog(company)}>
                  <TableCell className="font-medium">{company.name}</TableCell>
                  <TableCell>{company.naicCode}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          summary={rangeSummary('companies')}
        />
      </CardContent>
      <NaicCompanyDialog open={dialogOpen} onOpenChange={setDialogOpen} company={editing} />
    </Card>
  )
}
